import styled, { useTheme } from 'styled-components';
import { useState, useEffect } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useDispatch } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import {
  useCreateKycLinkMutation,
  useGetKycStatusQuery,
} from '../../redux/api/authorized/bridge';
import { BRIDGE_TOS_CALLBACK } from '../BridgeCallback';
import { BRIDGE_KYC_CALLBACK } from '../BridgeKycCallback';
import { BodyLarge, ParagraphLargeText } from '../Texts';
import Button from '../Button';
import LoadingSpinner from '../LoadingSpinner';
import TextInput from '../Inputs/Text';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
  @media ${PHONE_MEDIA_QUERY} {
    height: 100vh;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 10px 0px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-bottom: 0px;
  }
`;

const StepDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ active, theme }) =>
    active ? theme.primary.accent : theme.border.secondary};
`;

const TosIframe = styled.iframe`
  width: 100%;
  height: 500px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  margin: 16px 0;
`;

const KycIframe = styled.iframe`
  width: 100%;
  height: 600px;
  margin: 16px 0;
  @media ${PHONE_MEDIA_QUERY} {
    margin: 8px 0;
    height: 80%;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 14px;
  text-align: center;
  margin-top: 12px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FieldError = styled.div`
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
  display: flex;
  align-items: center;
`;

const ErrorContainer = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.status.error.background};
  border: 1px solid ${({ theme }) => theme.status.error.background};
  border-radius: 8px;
  margin-top: 16px;
`;

const KycVerification = ({ children, onClose }) => {
  const theme = useTheme();
  const { user } = usePrivy();
  const dispatch = useDispatch();
  const [createKycLink] = useCreateKycLinkMutation();

  const [step, setStep] = useState(0); // Start at 0 for auto-check
  const [bridgeCustomerId, setBridgeCustomerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tosIframeUrl, setTosIframeUrl] = useState(null);
  const [kycIframeUrl, setKycIframeUrl] = useState(null);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  // Get KYC status if user is authenticated
  const {
    data: kycStatus,
    isLoading: kycStatusLoading,
    error: kycStatusError,
  } = useGetKycStatusQuery(undefined, {
    skip: !user,
    pollingInterval: 3000,
  });

  // Get user email from Privy
  const userEmail = user?.email?.address || user?.google?.email || user?.email;

  // Step 1 - KYC Link Creation Schema
  const kycSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(kycSchema),
    defaultValues: {
      email: userEmail || '',
    },
  });

  // Auto-determine flow based on KYC status
  useEffect(() => {
    if (step === 0 && user) {
      if (kycStatusError) {
        // KYC status query failed (likely 404 - no KYC link exists)
        if (userEmail) {
          // Create KYC link with user's email
          handleKycSubmit({ email: userEmail });
        } else {
          setStep(1);
        }
      } else if (!kycStatusLoading && kycStatus) {
        // Got KYC status, handle response
        handleKycResponse(kycStatus);
      } else if (!kycStatusLoading && !kycStatus) {
        // Query completed but returned no data, create new KYC link
        if (userEmail) {
          // Create KYC link with user's email
          handleKycSubmit({ email: userEmail });
        } else {
          setStep(1);
        }
      }
      // Still loading KYC status - wait
    }
  }, [step, user, kycStatus, kycStatusLoading, kycStatusError, userEmail]);

  useEffect(() => {
    // Listen for iframe callback messages
    const handleMessage = (event) => {
      if (event.data?.type === BRIDGE_TOS_CALLBACK) {
        if (event.data.success && event.data.signedAgreementId) {
          setLoading(false);
          // TOS completed, check if we need KYC verification
          if (kycStatus?.kyc_status === 'approved' && bridgeCustomerId) {
            // KYC and TOS complete, render children
            return;
          } else if (kycStatus?.kyc_link) {
            setupKycIframe(kycStatus.kyc_link);
            setStep(3); // Go to KYC iframe
          } else {
            // Fallback to children
            return;
          }
        } else {
          setLoading(false);
        }
      } else if (event.data?.type === BRIDGE_KYC_CALLBACK) {
        if (event.data.success && event.data.kycLinkId) {
          setLoading(false);
          // KYC complete, render children
          return;
        } else {
          setLoading(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [kycStatus, bridgeCustomerId]);

  const setupTosIframe = (tosUrl) => {
    // Append redirect_uri as query parameter to the TOS URL
    const tosUrlWithRedirect = new URL(tosUrl);
    const redirectUri = `${window.location.origin}/bridge-tos-callback`;
    tosUrlWithRedirect.searchParams.append('redirect_uri', redirectUri);

    setTosIframeUrl(tosUrlWithRedirect.toString());
  };

  const setupKycIframe = (kycUrl) => {
    // Convert KYC link to iframe format
    // Replace /verify with /widget and add iframe-origin parameter
    const iframeUrl = new URL(kycUrl.replace('/verify', '/widget'));
    iframeUrl.searchParams.append('iframe-origin', window.location.origin);

    setKycIframeUrl(iframeUrl.toString());
  };

  const handleKycResponse = (response) => {
    // Set customer ID if available
    if (response.customer_id) {
      setBridgeCustomerId(response.customer_id);
    }

    // Check if user can proceed (both KYC and TOS complete)
    if (response.canCreateTransfers) {
      // Both TOS and KYC are complete, render children
      setLoading(false);
      return;
    }

    // Check TOS status first - if pending, show TOS
    if (response.tos_status === 'pending' && response.tos_link) {
      setStep(2); // Step 2 is TOS step
      setupTosIframe(response.tos_link);
      setLoading(false);
      return;
    }

    // If TOS approved but KYC not complete, handle KYC verification
    if (response.kyc_status === 'approved') {
      // KYC already complete, render children
      setLoading(false);
      return;
    }

    if (response.kyc_status === 'rejected') {
      dispatch(
        openModal({
          name: 'kycRejection',
        }),
      );
      setLoading(false);
      return;
    }

    // If KYC not started or incomplete, show KYC iframe
    if (
      ['not_started', 'incomplete'].includes(response.kyc_status) &&
      response.kyc_link
    ) {
      setupKycIframe(response.kyc_link);
      setStep(3); // Step 3 for KYC iframe
      setLoading(false);
    } else if (response.kyc_status === 'under_review') {
      setStep(6);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const handleKycSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const response = await createKycLink({
        email: data.email,
      }).unwrap();

      handleKycResponse(response);
    } catch (error) {
      if (error.data?.code === 'duplicate_record') {
        // We have a duplicate record - use the existing_kyc_link if available
        if (error.data?.existing_kyc_link?.kyc_link) {
          setupKycIframe(error.data.existing_kyc_link.kyc_link);
          setStep(3);
          setLoading(false);
          return;
        }

        // If we have existing_kyc_link but no kyc_link, try to get status
        if (error.data?.existing_kyc_link) {
          try {
            const statusResponse = await useGetKycStatusQuery().unwrap();
            handleKycResponse(statusResponse);
            return;
          } catch (statusError) {
            setError('Failed to retrieve existing verification status');
          }
        } else {
          setError('Unable to retrieve existing verification link');
        }
      } else {
        setError(error.data?.message || 'Failed to start verification process');
      }
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    if (step === 1) {
      handleKycSubmit(data);
    }
  };

  // If KYC and TOS are complete, render children
  if (kycStatus?.canCreateTransfers && bridgeCustomerId) {
    return children({ bridgeCustomerId });
  }

  // Otherwise render KYC steps
  return (
    <ContentContainer>
      {step > 0 && step <= 3 && (
        <StepIndicator>
          {[1, 2, 3].map((num) => (
            <StepDot
              key={num}
              active={
                (step === 1 && num === 1) || // Email step
                ((step === 2 || step === 3) && num === 2) || // TOS/KYC verification step
                (step === 3 && num === 3) // KYC completion
              }
            />
          ))}
        </StepIndicator>
      )}

      {step === 6 && (
        <>
          <ParagraphLargeText>Verifying Your Identity</ParagraphLargeText>
          <BodyLarge>
            Your verification is in progress. This process typically takes
            between 1 minute and 1 day. We'll notify you as soon as it's
            complete!
          </BodyLarge>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '20px 0',
            }}
          >
            <LoadingSpinner />
          </div>
        </>
      )}

      {step === 0 && (
        <>
          <ParagraphLargeText>Bridge Setup</ParagraphLargeText>
          <BodyLarge>
            {kycStatusLoading
              ? 'Checking verification status...'
              : 'Setting up your verification process...'}
          </BodyLarge>
          {!error && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '20px 0',
              }}
            >
              <LoadingSpinner />
            </div>
          )}
          {error && (
            <ErrorContainer>
              <BodyLarge
                style={{
                  color: theme.status.error.primary,
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                Verification Required
              </BodyLarge>
              <BodyLarge
                style={{
                  color: theme.status.error.primary,
                  fontSize: '14px',
                }}
              >
                {error}
              </BodyLarge>
              <Button
                onClick={() => {
                  setError(null);
                  setStep(1);
                }}
                style={{ marginTop: '12px' }}
              >
                Start Verification
              </Button>
            </ErrorContainer>
          )}
        </>
      )}

      {step === 1 && (
        <>
          <ParagraphLargeText>Bridge Verification</ParagraphLargeText>
          <BodyLarge>
            Enter your email to start the verification process.
          </BodyLarge>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='email'
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  label='Email'
                  placeholder='Enter your email'
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors.email?.message}
                />
              )}
            />

            <Button type='submit' fullWidth={true} disabled={loading}>
              {loading ? 'Creating verification link...' : 'Start Verification'}
            </Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {errors.email && <FieldError>{errors.email.message}</FieldError>}
          </Form>
        </>
      )}

      {step === 2 && (
        <>
          <ParagraphLargeText>Accept Terms of Service</ParagraphLargeText>
          <BodyLarge>
            Please review and accept Bridge's Terms of Service to continue.
          </BodyLarge>

          {tosIframeUrl && (
            <TosIframe
              src={tosIframeUrl}
              title='Bridge Terms of Service'
              frameBorder='0'
            />
          )}

          <BodyLarge
            style={{
              textAlign: 'center',
              color: theme.text.medium,
              fontSize: '14px',
            }}
          >
            Complete the Terms of Service above to continue with verification.
          </BodyLarge>
        </>
      )}

      {step === 3 && (
        <>
          {!isMobile && (
            <>
              <ParagraphLargeText>
                Complete Identity Verification
              </ParagraphLargeText>
              <BodyLarge>
                Please complete your identity verification to continue.
              </BodyLarge>
            </>
          )}
          {kycIframeUrl && (
            <KycIframe
              src={kycIframeUrl}
              title='Bridge Identity Verification'
              frameBorder='0'
              allow='camera;'
              sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation'
            />
          )}

          <BodyLarge
            style={{
              textAlign: 'center',
              color: theme.text.medium,
              fontSize: '14px',
            }}
          >
            Complete the identity verification above to continue.
          </BodyLarge>
        </>
      )}
    </ContentContainer>
  );
};

export default KycVerification;
